import { useMutation } from "@tanstack/react-query";
import { useServices } from "@/app/use-services";
import { billingMessage } from "@/features/plan/billing-message";
import { SubscribeOffer } from "@/features/plan/SubscribeOffer";
import { usePlan } from "@/features/plan/use-plan";
import { errorMessage } from "@/lib/api/errors";
import { describeTalkTime, type UserPlan } from "@/lib/api/models/plan";
import { leaveFor } from "@/lib/navigation";
import { BackLink } from "@/ui/BackLink";
import { Button } from "@/ui/Button";
import { ErrorView } from "@/ui/ErrorView";
import { Block, ErrorNote } from "@/ui/Field";
import { Page } from "@/ui/Page";
import { Body, Display, Label, Tiny } from "@/ui/Text";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function formatDay(date: Date): string {
  return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
}

/** The plan: what is left, and either the offer or the way to manage the subscription. */
export function PlanPage() {
  const plan = usePlan();
  return (
    <Page>
      <BackLink to="/you" label="You" />
      <Label className="mt-3">Your plan</Label>
      {plan.isPending && <Body className="mt-3">Reading your plan…</Body>}
      {plan.isError && <ErrorView message={errorMessage(plan.error)} onRetry={() => void plan.refetch()} />}
      {plan.data && (plan.data.active ? <Subscribed plan={plan.data} /> : <Free plan={plan.data} />)}
    </Page>
  );
}

function Free({ plan }: { readonly plan: UserPlan }) {
  return (
    <div className="mt-4 space-y-6">
      <Block label="Free time">
        <Display size="md" as="p">{describeTalkTime(plan)}</Display>
        <Body className="mt-2 text-mid">Three minutes to talk or type, once. After that, the plan keeps everything open.</Body>
      </Block>
      <SubscribeOffer />
    </div>
  );
}

function Subscribed({ plan }: { readonly plan: UserPlan }) {
  const { api, analytics } = useServices();
  const manage = useMutation({
    mutationFn: () => api.openBillingPortal(),
    onSuccess: (url) => {
      analytics.track({ name: "billing portal opened" });
      leaveFor(url);
    },
  });
  return (
    <div className="mt-4 space-y-4">
      <Block label="This month">
        <Display size="md" as="p">{describeTalkTime(plan)}</Display>
        <Body className="mt-2">Type as much as you like. Voice starts again at an hour on the 1st of each month.</Body>
      </Block>
      {plan.expires_at && (
        <Block>
          <Body>{plan.renews ? `Renews ${formatDay(plan.expires_at)}.` : `Cancelled. You keep everything until ${formatDay(plan.expires_at)}.`}</Body>
        </Block>
      )}
      <Button label="Manage subscription" outlined onClick={() => manage.mutate()} busy={manage.isPending} />
      <ErrorNote message={manage.isError ? billingMessage(manage.error) : null} />
      <Tiny>Bought on a phone? Manage it in that phone’s app store instead.</Tiny>
    </div>
  );
}
